// Servicio HTTP del Pricer Datec.

import { apiClient, axiosInstance } from '@/lib/api';
import type {
  RespuestaImportacion,
  PricerCatalog,
  PricerDatecQuoteSummary,
  PricerDatecQuoteFull,
  PricerParams,
  PricerItem,
  GarantiaItem,
  PricerDatecVersionSummary,
  PricerDatecVersionFull,
  ReferenceRates,
  RespuestaEnvio,
  AvisoDePresupuesto,
  TramosDelAviso,
  FilaDeLaMatriz,
  AjustesPricer,
  RespuestaAjustes,
  PresentacionDocumento,
  CambioDeCostos,
  PrevisualizacionDeEnmienda,
} from '../types/pricer-datec.types';

const BASE = '/api/pricer-datec';

export interface AvisoDeEnvio {
  clave: 'sinLinea' | 'costoCero';
  texto: string;
  implicancia: string;
  items: Array<{ itemId?: number; num: number; descripcion: string }>;
}

/** Lo que devuelve el aviso previo de aprobación. */
export interface VistaPreviaAprobacion {
  requiereAprobacion: boolean;
  /** Un paso de la cadena que nadie puede firmar: el envío se rechazaría. El texto lo arma el servidor. */
  cadenaTrabada?: { mensaje: string } | null;
  /** El margen que de verdad se controla. */
  margenOperativo: number;
  /** El que se ve en grande en el encabezado. Suele ser más alto. */
  margenBruto: number;
  motivo: string | null;
  firmasRequeridas: number;
  reglasAdicionales: Array<{ id: string; name: string; triggerReason: string }>;
  aprobadores: Array<{ id: string; firstName: string; lastName: string }>;
  peorLinea: { numero: number; descripcion: string; margen: number } | null;
  /** El presupuesto del cliente contra el total del documento (F6 T13): avisa; pide firma sólo con una regla de presupuesto. */
  presupuesto?: AvisoDePresupuesto;
  /** LOB que el envío rechazaría (desactivados o inexistentes), con los ítems. */
  lobsNoValidos?: string[];
  /** Lote D (A1, A2): lo que NO impide enviar pero hay que saber (sin línea de negocio, con costo 0). */
  avisos?: AvisoDeEnvio[];
  // ── Contrato que F6 T17 entrega (plan F5 §16.1). TODO OPCIONAL: con la respuesta de hoy no vienen,
  // y `utils/adaptar-firmas.ts` es el único lugar que los lee. ──
  /** Las firmas que pide el envío, ya resueltas con la misma lógica que el servidor (con el pool). */
  firmas?: FirmaDelServidor[];
  /** Si las firmas de las reglas en juego se suman. */
  seSuman?: boolean;
  /** Filas vacías que el envío quita, y la numeración 1..n es la del servidor. */
  filasVaciasQuitadas?: number;
  /** Margen, tramo y firma de cada línea (numeración del servidor). */
  lineas?: Array<{ numero: number; margen?: number; tramo?: string; firma?: string }>;
  /** F6 T17: el número que mira la regla y cuánto falta para el tramo, por línea y para el total. */
  tramos?: TramosDelAviso;
  /** Las reglas de aprobación configuradas de la empresa (sólo lectura), con la que aplica y la que firma marcadas. */
  matriz?: FilaDeLaMatriz[];
}

export interface FirmaDelServidor {
  id: string;
  motivo: string;
  origen: 'REGLA' | 'PISO_LOB' | 'MODO_EMPRESA';
  regla?: { id: string; nombre: string };
  lobs: string[];
  lineas: number[];
  tipo: 'PERSONAS' | 'POOL';
  poolTamano?: number;
  firmantes: Array<{ userId: string; nombre: string; cargo?: string }>;
}

interface QuotePayload {
  params: PricerParams;
  items: PricerItem[];
  garantias: GarantiaItem[];
  // Nombre descriptivo opcional para identificar la cotizacion en listados.
  name?: string | null;
  // ID interno (UUID) del registro HubspotDeal. Obligatorio al enviar a aprobación.
  hubspotDealId?: string | null;
  // La ficha de cliente elegida (params.cliente queda como foto del nombre).
  customerId?: string | null;
  // Descripción interna (no sale en el PDF del cliente).
  internalDescription?: string | null;
  // El tope del cliente, en USD (F6 T13); null lo quita.
  presupuestoCliente?: number | null;
  // La revisión que la pantalla tenía abierta: si otra pestaña cambió la cotización, el servidor responde 409.
  revision?: number;
  /** La huella de las reglas con las que calculó la pantalla y la de la propuesta de flete: el servidor avisa si cambiaron. */
  reglasHash?: string;
  fleteHash?: string;
  /** La orden explícita de aceptar el cálculo nuevo (la acción de la banda). */
  actualizar?: { reglas?: boolean; flete?: 'FALTANTE' | 'TODOS' };
  /** El aviso previo con el id usa lo guardado de esa cotización (mismas reglas que el guardado). */
  quoteId?: string;
  /** Cómo se ve el documento al cliente (una columna aparte, sin dinero). */
  presentacion?: PresentacionDocumento;
}

class PricerDatecService {
  /** La configuración del Pricer de la empresa (formato del CITE y textos por defecto). */
  async getAjustes(): Promise<RespuestaAjustes> {
    return apiClient.get<RespuestaAjustes>(`${BASE}/ajustes`);
  }

  /** Sólo el administrador de la empresa; el servidor responde 403 a cualquier otro. */
  async guardarAjustes(ajustes: AjustesPricer): Promise<RespuestaAjustes> {
    return apiClient.put<RespuestaAjustes>(`${BASE}/ajustes`, ajustes);
  }

  async getCatalog(): Promise<PricerCatalog> {
    return apiClient.get<PricerCatalog>(`${BASE}/catalog`);
  }

  /**
   * El TC oficial del BCB y el USDC paralelo.
   *
   * Vive en master-data, no en el pricer: son datos de moneda de la plataforma,
   * no del pricer. Cualquiera de los dos puede venir en null.
   */
  async getReferenceRates(): Promise<ReferenceRates> {
    return apiClient.get<ReferenceRates>('/api/master-data/currencies/reference-rates');
  }

  /** La plantilla de carga masiva, con el catálogo de este tenant adentro. */
  /**
   * Con `quoteId`, la plantilla baja CON los ítems de esa cotización, para
   * editarlos y subirlos de vuelta. Sin él, baja vacía con ejemplos.
   */
  async descargarPlantilla(quoteId?: string | null): Promise<Blob> {
    // 🔴 Directo contra axios, no por `apiClient`: ese devuelve
    // `response.data.data`, y en una respuesta binaria `response.data` YA es el
    // Blob y no tiene `.data` — la descarga saldría `undefined` sin fallar.
    const res = await axiosInstance.get(`${BASE}/items/template`, {
      responseType: 'blob',
      params: quoteId ? { quoteId } : undefined,
    });
    return res.data as Blob;
  }

  /** Lee la planilla. No toca la cotización: devuelve lo leído para revisar. */
  async importarPlantilla(file: File): Promise<RespuestaImportacion> {
    const form = new FormData();
    form.append('file', file);
    return apiClient.post<RespuestaImportacion>(`${BASE}/items/import`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60_000,
    });
  }

  async listQuotes(opts?: {
    status?: string;
    hasDeal?: 'true' | 'false';
  }): Promise<PricerDatecQuoteSummary[]> {
    const params = new URLSearchParams();
    if (opts?.status) params.set('status', opts.status);
    if (opts?.hasDeal) params.set('hasDeal', opts.hasDeal);
    const q = params.toString() ? `?${params.toString()}` : '';
    return apiClient.get<PricerDatecQuoteSummary[]>(`${BASE}/quotes${q}`);
  }

  /** El espejo se reconoce por el vínculo real (N2): `null` si el `Quote` no es el espejo de una cotización del Pricer. */
  async resolverEspejo(quoteId: string): Promise<{ id: string; code: string } | null> {
    return apiClient.get<{ id: string; code: string } | null>(`${BASE}/espejo/${quoteId}`);
  }

  /** La ficha de cliente que corresponde a una oportunidad, sólo si la tiene. */
  async getClienteDeOportunidad(dealId: string): Promise<{
    empresa: string | null;
    cliente: { id: string; name: string; code?: string | null } | null;
  }> {
    return apiClient.get(`${BASE}/deals/${dealId}/customer`);
  }

  async getQuote(id: string): Promise<PricerDatecQuoteFull> {
    return apiClient.get<PricerDatecQuoteFull>(`${BASE}/quotes/${id}`);
  }

  async createQuote(data: QuotePayload): Promise<PricerDatecQuoteFull> {
    return apiClient.post<PricerDatecQuoteFull>(`${BASE}/quotes`, data);
  }

  async updateQuote(id: string, data: Partial<QuotePayload>): Promise<PricerDatecQuoteFull> {
    return apiClient.patch<PricerDatecQuoteFull>(`${BASE}/quotes/${id}`, data);
  }

  async deleteQuote(id: string): Promise<void> {
    await apiClient.delete(`${BASE}/quotes/${id}`);
  }

  /** Pide reabrir una cotización aprobada. El backend responde `{ data: { approvalId } }`. */
  async requestReopen(quoteId: string, motivo: string): Promise<{ approvalId: string }> {
    return apiClient.post<{ approvalId: string }>(`${BASE}/quotes/${quoteId}/reopen-request`, {
      motivo,
    });
  }

  /**
   * Reabre SIN firma una aprobada (16-oct). Sólo si `permisos.puedeReabrirSinFirma`; el servidor lo vuelve a decidir
   * con el nivel de la cotización y responde 403 si no corresponde.
   */
  async reopen(
    quoteId: string,
    motivo: string
  ): Promise<{ reabierta: boolean; nivel: number; versionId: string }> {
    return apiClient.post(`${BASE}/quotes/${quoteId}/reopen`, { motivo });
  }

  /** Cancela una cotización en aprobación, con ajuste solicitado o rechazada (motivo obligatorio). */
  async cancelQuote(quoteId: string, motivo: string, revision: number): Promise<void> {
    await apiClient.post(`${BASE}/quotes/${quoteId}/cancel`, { motivo, revision });
  }

  /** Enmienda de costos de una aprobada (T18): qué pasaría con estos cambios. No escribe nada. */
  async previewEnmienda(
    quoteId: string,
    cambios: CambioDeCostos[],
    revision?: number
  ): Promise<PrevisualizacionDeEnmienda> {
    return apiClient.post<PrevisualizacionDeEnmienda>(`${BASE}/quotes/${quoteId}/amend/preview`, {
      cambios,
      ...(revision !== undefined ? { revision } : {}),
    });
  }

  /** Aplica la enmienda de costos: el precio no cambia y la cotización sigue aprobada. */
  async aplicarEnmienda(
    quoteId: string,
    cambios: CambioDeCostos[],
    motivo: string,
    revision?: number
  ): Promise<void> {
    await apiClient.post(`${BASE}/quotes/${quoteId}/amend`, {
      cambios,
      motivo,
      ...(revision !== undefined ? { revision } : {}),
    });
  }

  /** «Retirar para corregir»: sólo quien la creó, y mientras nadie firmó ningún paso. */
  async withdrawQuote(quoteId: string): Promise<void> {
    await apiClient.post(`${BASE}/quotes/${quoteId}/withdraw`, {});
  }

  async submitForApproval(
    id: string,
    options: { requestNote?: string | null; revision: number }
  ): Promise<RespuestaEnvio> {
    return apiClient.post<RespuestaEnvio>(`${BASE}/quotes/${id}/submit`, {
      requestNote: options.requestNote ?? null,
      revision: options.revision,
    });
  }

  /**
   * Qué va a pasar si mando esto a aprobación — sin mandarlo.
   *
   * Corre exactamente la misma evaluación que el envío de verdad (misma función
   * en el backend), así que el aviso no puede desmentirse a sí mismo. No escribe
   * nada.
   */
  async previewApproval(data: QuotePayload): Promise<VistaPreviaAprobacion> {
    return apiClient.post<VistaPreviaAprobacion>(`${BASE}/quotes/preview-approval`, data);
  }

  /**
   * La matriz de aprobación (y los tramos) de una cotización que se mira en SÓLO LECTURA, evaluada con lo GUARDADO.
   * Es una lectura: llega a quien firma con acceso de lectura. El servidor decide quién la recibe (quien ve costos y es
   * parte de la aprobación); a cualquier otro le responde `matriz: null`.
   */
  async aprobacionPrevia(
    quoteId: string
  ): Promise<{ tramos: TramosDelAviso | null; matriz: FilaDeLaMatriz[] | null }> {
    return apiClient.get(`${BASE}/quotes/${quoteId}/aprobacion-previa`);
  }

  // ── Versiones / hitos ─────────────────────────────────────────
  async listVersions(quoteId: string): Promise<PricerDatecVersionSummary[]> {
    return apiClient.get<PricerDatecVersionSummary[]>(`${BASE}/quotes/${quoteId}/versions`);
  }

  async getVersion(quoteId: string, versionId: string): Promise<PricerDatecVersionFull> {
    return apiClient.get<PricerDatecVersionFull>(`${BASE}/quotes/${quoteId}/versions/${versionId}`);
  }

  async createMilestone(
    quoteId: string,
    label: string,
    changeNote?: string
  ): Promise<PricerDatecVersionFull> {
    return apiClient.post<PricerDatecVersionFull>(`${BASE}/quotes/${quoteId}/versions`, {
      label,
      changeNote: changeNote || null,
    });
  }

  async restoreVersion(
    quoteId: string,
    versionId: string,
    revision: number
  ): Promise<PricerDatecQuoteFull> {
    return apiClient.post<PricerDatecQuoteFull>(
      `${BASE}/quotes/${quoteId}/versions/${versionId}/restore`,
      { revision }
    );
  }

  // ── Adjuntos ─────────────────────────────────────────────────────
  async listAttachments(quoteId: string): Promise<PricerDatecAttachment[]> {
    return apiClient.get<PricerDatecAttachment[]>(`${BASE}/${quoteId}/attachments`);
  }

  async uploadAttachment(
    quoteId: string,
    file: File,
    title: string,
    description?: string
  ): Promise<PricerDatecAttachment> {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('title', title);
    if (description) fd.append('description', description);
    return apiClient.post<PricerDatecAttachment>(`${BASE}/${quoteId}/attachments`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  async updateAttachment(
    quoteId: string,
    attachmentId: string,
    data: { title?: string; description?: string | null }
  ): Promise<PricerDatecAttachment> {
    return apiClient.patch<PricerDatecAttachment>(
      `${BASE}/${quoteId}/attachments/${attachmentId}`,
      data
    );
  }

  async deleteAttachment(quoteId: string, attachmentId: string): Promise<void> {
    return apiClient.delete<void>(`${BASE}/${quoteId}/attachments/${attachmentId}`);
  }

  // El download retorna un archivo binario — el componente lo abrirá via window.open
  getAttachmentDownloadUrl(quoteId: string, attachmentId: string): string {
    return `${BASE}/${quoteId}/attachments/${attachmentId}/download`;
  }

  // ── Comentarios ─────────────────────────────────────────────────
  async listComments(quoteId: string): Promise<PricerDatecComment[]> {
    return apiClient.get<PricerDatecComment[]>(`${BASE}/${quoteId}/comments`);
  }

  async createComment(quoteId: string, body: string): Promise<PricerDatecComment> {
    return apiClient.post<PricerDatecComment>(`${BASE}/${quoteId}/comments`, { body });
  }

  async updateComment(
    quoteId: string,
    commentId: string,
    body: string
  ): Promise<PricerDatecComment> {
    return apiClient.patch<PricerDatecComment>(`${BASE}/${quoteId}/comments/${commentId}`, {
      body,
    });
  }

  async deleteComment(quoteId: string, commentId: string): Promise<void> {
    return apiClient.delete<void>(`${BASE}/${quoteId}/comments/${commentId}`);
  }

  // ── Panel lateral (T7) ───────────────────────────────────────────
  /** El historial ya traducido por el servidor (lista blanca). */
  async getHistorial(quoteId: string): Promise<RenglonDelHistorial[]> {
    return apiClient.get<RenglonDelHistorial[]>(`${BASE}/quotes/${quoteId}/historial`);
  }

  /** Cuántos adjuntos y comentarios tiene, para el contador de la cabecera. */
  async getConteoLateral(quoteId: string): Promise<ConteoLateral> {
    return apiClient.get<ConteoLateral>(`${BASE}/quotes/${quoteId}/panel-lateral/conteo`);
  }
}

export interface RenglonDelHistorial {
  id: string;
  tipo: string;
  texto: string;
  /** ISO. */
  cuando: string;
  quien: string | null;
  /** Una descarga de documento: la pantalla la oculta por omisión. */
  esDescarga: boolean;
}

export interface ConteoLateral {
  adjuntos: number;
  comentarios: number;
}

// ── Tipos compartidos por componentes ────────────────────────────
export interface PricerDatecAttachment {
  id: string;
  quoteId: string;
  title: string;
  description: string | null;
  originalName: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  uploadedById: string;
  uploadedBy: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface PricerDatecComment {
  id: string;
  quoteId: string;
  body: string;
  authorId: string;
  author: { id: string; firstName: string; lastName: string; email: string };
  createdAt: string;
  updatedAt: string;
}

export const pricerDatecService = new PricerDatecService();
